import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  Calendar,
  Clock,
  User,
  Video,
  DollarSign,
  CheckCircle,
  ArrowRight,
  X,
  Sparkles
} from 'lucide-react';

const ONBOARDING_KEY = 'drbooking_pro_onboarding_dismissed';

const texts = {
  fr: {
    title: 'Bienvenue sur drbooking !',
    subtitle: 'Configurez votre espace en quelques étapes pour recevoir vos premiers patients.',
    step1_title: '1. Définir vos disponibilités',
    step1_desc: 'Sélectionnez une date dans le calendrier, définissez vos horaires (ex: 9h-17h) puis cliquez sur "Générer créneaux" et "Enregistrer".',
    step2_title: '2. Compléter votre profil',
    step2_desc: 'Ajoutez votre photo, votre spécialité, votre adresse et vos langues parlées pour être visible par les patients.',
    step3_title: '3. Définir vos tarifs',
    step3_desc: 'Indiquez le prix de vos consultations (cabinet, domicile, téléconsultation) dans les paramètres.',
    step4_title: '4. Activer la téléconsultation',
    step4_desc: 'Activez la vidéo-consultation pour consulter vos patients à distance depuis n\'importe quel pays de l\'UE.',
    important: 'Important',
    important_text: 'Sans disponibilités configurées, les patients verront "Aucun créneau disponible" sur votre agenda.',
    got_it: 'J\'ai compris, commencer',
    dismiss: 'Ne plus afficher',
    step: 'Étape'
  },
  en: {
    title: 'Welcome to drbooking!',
    subtitle: 'Set up your space in a few steps to receive your first patients.',
    step1_title: '1. Set your availability',
    step1_desc: 'Select a date on the calendar, set your hours (e.g. 9am-5pm), then click "Generate slots" and "Save".',
    step2_title: '2. Complete your profile',
    step2_desc: 'Add your photo, specialty, address and spoken languages to be visible to patients.',
    step3_title: '3. Set your rates',
    step3_desc: 'Enter the price of your consultations (office, home, teleconsultation) in settings.',
    step4_title: '4. Enable teleconsultation',
    step4_desc: 'Enable video consultations to see patients remotely from any EU country.',
    important: 'Important',
    important_text: 'Without configured availability, patients will see "No slots available" on your calendar.',
    got_it: 'Got it, let\'s start',
    dismiss: 'Don\'t show again',
    step: 'Step'
  },
  de: {
    title: 'Willkommen bei drbooking!',
    subtitle: 'Richten Sie Ihren Bereich in wenigen Schritten ein, um Ihre ersten Patienten zu empfangen.',
    step1_title: '1. Verfügbarkeit festlegen',
    step1_desc: 'Wählen Sie ein Datum im Kalender, legen Sie Ihre Zeiten fest (z.B. 9-17 Uhr), dann klicken Sie auf "Slots generieren" und "Speichern".',
    step2_title: '2. Profil vervollständigen',
    step2_desc: 'Fügen Sie Ihr Foto, Fachgebiet, Adresse und gesprochene Sprachen hinzu.',
    step3_title: '3. Tarife festlegen',
    step3_desc: 'Geben Sie den Preis Ihrer Konsultationen (Praxis, Hausbesuch, Telekonsultation) in den Einstellungen an.',
    step4_title: '4. Telekonsultation aktivieren',
    step4_desc: 'Aktivieren Sie Videokonsultationen, um Patienten aus jedem EU-Land fernzuberaten.',
    important: 'Wichtig',
    important_text: 'Ohne konfigurierte Verfügbarkeit sehen Patienten "Keine Termine verfügbar" in Ihrem Kalender.',
    got_it: 'Verstanden, los geht\'s',
    dismiss: 'Nicht mehr anzeigen',
    step: 'Schritt'
  },
  es: {
    title: 'Bienvenido a drbooking!',
    subtitle: 'Configure su espacio en pocos pasos para recibir a sus primeros pacientes.',
    step1_title: '1. Definir disponibilidad',
    step1_desc: 'Seleccione una fecha en el calendario, defina sus horarios (ej: 9h-17h), luego haga clic en "Generar franjas" y "Guardar".',
    step2_title: '2. Completar perfil',
    step2_desc: 'Agregue su foto, especialidad, dirección e idiomas hablados para ser visible para los pacientes.',
    step3_title: '3. Definir tarifas',
    step3_desc: 'Indique el precio de sus consultas (consultorio, domicilio, teleconsulta) en la configuración.',
    step4_title: '4. Activar teleconsulta',
    step4_desc: 'Active la videoconsulta para atender pacientes a distancia desde cualquier país de la UE.',
    important: 'Importante',
    important_text: 'Sin disponibilidad configurada, los pacientes verán "Sin citas disponibles" en su agenda.',
    got_it: 'Entendido, empezar',
    dismiss: 'No mostrar más',
    step: 'Paso'
  },
  pt: {
    title: 'Bem-vindo ao drbooking!',
    subtitle: 'Configure o seu espaço em poucos passos para receber os seus primeiros pacientes.',
    step1_title: '1. Definir disponibilidade',
    step1_desc: 'Selecione uma data no calendário, defina os seus horários (ex: 9h-17h), depois clique em "Gerar slots" e "Guardar".',
    step2_title: '2. Completar perfil',
    step2_desc: 'Adicione a sua foto, especialidade, morada e idiomas falados para ser visível pelos pacientes.',
    step3_title: '3. Definir tarifas',
    step3_desc: 'Indique o preço das suas consultas (consultório, domicílio, teleconsulta) nas configurações.',
    step4_title: '4. Ativar teleconsulta',
    step4_desc: 'Ative a videoconsulta para consultar pacientes à distância de qualquer país da UE.',
    important: 'Importante',
    important_text: 'Sem disponibilidade configurada, os pacientes verão "Sem horários disponíveis" na sua agenda.',
    got_it: 'Entendi, começar',
    dismiss: 'Não mostrar mais',
    step: 'Passo'
  },
  it: {
    title: 'Benvenuto su drbooking!',
    subtitle: 'Configura il tuo spazio in pochi passaggi per ricevere i tuoi primi pazienti.',
    step1_title: '1. Definire le disponibilità',
    step1_desc: 'Seleziona una data nel calendario, imposta i tuoi orari (es: 9-17), poi clicca su "Genera slot" e "Salva".',
    step2_title: '2. Completare il profilo',
    step2_desc: 'Aggiungi la tua foto, specialità, indirizzo e lingue parlate per essere visibile ai pazienti.',
    step3_title: '3. Definire le tariffe',
    step3_desc: 'Indica il prezzo delle tue consulte (studio, domicilio, teleconsulto) nelle impostazioni.',
    step4_title: '4. Attivare il teleconsulto',
    step4_desc: 'Attiva la videoconsulenza per visitare pazienti a distanza da qualsiasi paese dell\'UE.',
    important: 'Importante',
    important_text: 'Senza disponibilità configurate, i pazienti vedranno "Nessun appuntamento disponibile" nella tua agenda.',
    got_it: 'Ho capito, iniziamo',
    dismiss: 'Non mostrare più',
    step: 'Passo'
  },
  ar: {
    title: 'مرحباً بك في drbooking!',
    subtitle: 'قم بإعداد مساحتك في خطوات قليلة لاستقبال مرضاك الأوائل.',
    step1_title: '1. تحديد المواعيد المتاحة',
    step1_desc: 'اختر تاريخاً في التقويم، حدد ساعات العمل (مثلاً 9-17)، ثم انقر على "إنشاء الفترات" و "حفظ".',
    step2_title: '2. إكمال الملف الشخصي',
    step2_desc: 'أضف صورتك وتخصصك وعنوانك واللغات التي تتحدثها لتكون مرئياً للمرضى.',
    step3_title: '3. تحديد الأسعار',
    step3_desc: 'أدخل أسعار استشاراتك (عيادة، منزل، استشارة عن بعد) في الإعدادات.',
    step4_title: '4. تفعيل الاستشارة عن بعد',
    step4_desc: 'فعّل الاستشارة بالفيديو لرؤية المرضى عن بعد من أي دولة في الاتحاد الأوروبي.',
    important: 'مهم',
    important_text: 'بدون مواعيد محددة، سيرى المرضى "لا توجد مواعيد متاحة" في جدولك.',
    got_it: 'فهمت، لنبدأ',
    dismiss: 'لا تظهر مرة أخرى',
    step: 'خطوة'
  },
  lb: {
    title: 'Wëllkomm op drbooking!',
    subtitle: 'Riicht Ären Espace a puer Schrëtt an fir Är éischt Patientten ze empfänken.',
    step1_title: '1. Disponibilitéiten festleeën',
    step1_desc: 'Wielt en Datum am Kalenner, setzt Är Zäiten (z.B. 9-17 Auer), da klickt op "Créneauen generéieren" an "Späicheren".',
    step2_title: '2. Profil kompletéieren',
    step2_desc: 'Setzt Äert Foto, Fachgebitt, Adress an Sproochen derbäi fir sichtbar ze sinn.',
    step3_title: '3. Tariffer festleeën',
    step3_desc: 'Gitt de Präis vun Ären Consultatiounen un (Cabinet, Doheem, Telekonsultatioun).',
    step4_title: '4. Telekonsultatioun aktivéieren',
    step4_desc: 'Aktivéiert d\'Video-Consultatioun fir Patientten aus all EU-Land op Distanz ze beroden.',
    important: 'Wichteg',
    important_text: 'Ouni konfiguréiert Disponibilitéiten gesinn Patientten "Keen Rendez-vous disponibel" an Ärem Kalenner.',
    got_it: 'Verstanen, lass ufänken',
    dismiss: 'Net méi weisen',
    step: 'Schrëtt'
  }
};

const steps = [
  { icon: Calendar, color: 'bg-blue-100 text-blue-600', key: 'step1' },
  { icon: User, color: 'bg-emerald-100 text-emerald-600', key: 'step2' },
  { icon: DollarSign, color: 'bg-amber-100 text-amber-600', key: 'step3' },
  { icon: Video, color: 'bg-purple-100 text-purple-600', key: 'step4' }
];

const ProfessionalOnboarding = ({ userId }) => {
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const ct = texts[language] || texts.fr;

  useEffect(() => {
    const dismissed = localStorage.getItem(`${ONBOARDING_KEY}_${userId}`);
    if (!dismissed) {
      setVisible(true);
    }
  }, [userId]);

  const handleDismiss = () => {
    localStorage.setItem(`${ONBOARDING_KEY}_${userId}`, 'true');
    setVisible(false);
  };

  const handleGotIt = () => {
    localStorage.setItem(`${ONBOARDING_KEY}_${userId}`, 'true');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="pro-onboarding-modal">
      <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border-0">
        <CardContent className="p-0">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white relative">
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 text-white/70 hover:text-white"
              data-testid="onboarding-close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Sparkles className="w-7 h-7" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{ct.title}</h2>
                <p className="text-blue-100 text-sm">{ct.subtitle}</p>
              </div>
            </div>
            {/* Step indicators */}
            <div className="flex gap-2 mt-4">
              {steps.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentStep(idx)}
                  className={`h-1.5 flex-1 rounded-full transition-all ${
                    idx === currentStep ? 'bg-white' : idx < currentStep ? 'bg-white/60' : 'bg-white/20'
                  }`}
                  data-testid={`onboarding-step-indicator-${idx}`}
                />
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${steps[currentStep].color}`}>
                {React.createElement(steps[currentStep].icon, { className: 'w-7 h-7' })}
              </div>
              <div>
                <Badge variant="outline" className="mb-2 text-xs">
                  {ct.step} {currentStep + 1}/4
                </Badge>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  {ct[`${steps[currentStep].key}_title`]}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {ct[`${steps[currentStep].key}_desc`]}
                </p>
              </div>
            </div>

            {/* Important notice - show on step 1 */}
            {currentStep === 0 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6" data-testid="onboarding-important-notice">
                <p className="text-sm text-amber-800 font-medium flex items-start gap-2">
                  <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span><strong>{ct.important} :</strong> {ct.important_text}</span>
                </p>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between pt-4 border-t">
              <button
                onClick={handleDismiss}
                className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
                data-testid="onboarding-dismiss"
              >
                {ct.dismiss}
              </button>
              <div className="flex gap-2">
                {currentStep > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentStep(currentStep - 1)}
                    data-testid="onboarding-prev"
                  >
                    <ArrowRight className="w-4 h-4 rotate-180" />
                  </Button>
                )}
                {currentStep < steps.length - 1 ? (
                  <Button
                    size="sm"
                    onClick={() => setCurrentStep(currentStep + 1)}
                    className="gap-2"
                    data-testid="onboarding-next"
                  >
                    {ct.step} {currentStep + 2}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={handleGotIt}
                    className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="onboarding-complete"
                  >
                    <CheckCircle className="w-4 h-4" />
                    {ct.got_it}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfessionalOnboarding;
